import React, { useState, useEffect, useCallback, useRef } from 'react';
import logo from '../assets/images/logo.png';
import logoWhite from '../assets/images/logo_white.png';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiArrowUpRight } from 'react-icons/fi';
import {
  HiOutlineInformationCircle,
  HiOutlineShieldCheck,
  HiOutlineBookOpen,
  HiOutlineBeaker,
  HiOutlineDocumentText,
  HiOutlineSparkles,
  HiOutlineScale,
  HiOutlineLightningBolt,
  HiOutlineOfficeBuilding,
  HiOutlineBriefcase,
  HiOutlineHome,
  HiOutlineLockClosed,
  HiOutlineClipboardList,
} from 'react-icons/hi';
import styles from './Navbar.module.css';
import { NAV_ITEMS as NAV_ITEMS_CONFIG } from '../config/content';

interface DropdownItem {
  label: string;
  desc?: string;
  href: string;
  icon: React.ReactNode;
  external?: boolean;
}

interface NavItemConfig {
  label: string;
  href?: string;
  dropdown?: DropdownItem[];
}

const DROPDOWN_ICONS: Record<string, React.ReactNode> = {
  'Case Management': <HiOutlineClipboardList />,
  'Legal Research': <HiOutlineSparkles />,
  'Workflow': <HiOutlineLightningBolt />,
  'About Us': <HiOutlineInformationCircle />,
  'Practice Areas': <HiOutlineScale />,
  'Responsible AI': <HiOutlineShieldCheck />,
  'Data Privacy': <HiOutlineLockClosed />,
  'Blogs': <HiOutlineBookOpen />,
  'JurisTab': <HiOutlineBeaker />,
  'DocuDive': <HiOutlineDocumentText />,
  'In-House Lawyers': <HiOutlineOfficeBuilding />,
  'Private Practice': <HiOutlineBriefcase />,
  'Conveyancers': <HiOutlineHome />,
  'In-House Counsel': <HiOutlineOfficeBuilding />,
  'Law Firms': <HiOutlineBriefcase />,
  'Real Estate Closings': <HiOutlineHome />,
};

const NAV_ITEMS: NavItemConfig[] = NAV_ITEMS_CONFIG.map((item) => ({
  ...item,
  dropdown: item.dropdown?.map((sub) => ({
    ...sub,
    icon: DROPDOWN_ICONS[sub.label] || null,
  })),
}));

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileDropdowns, setMobileDropdowns] = useState<Set<string>>(new Set());
  const navRef = useRef<HTMLElement>(null);

  // Scroll listener
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 1024) {
        setMobileOpen(false);
        setMobileDropdowns(new Set());
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close desktop dropdown on click outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggleMobileDropdown = useCallback((label: string) => {
    setMobileDropdowns((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setMobileDropdowns(new Set());
  }, []);

  // Render helpers
  const renderLink = (href: string, children: React.ReactNode, className: string, external?: boolean, onClick?: () => void) => {
    if (external) {
      return (
        <a href={href} className={className} target="_blank" rel="noopener noreferrer" onClick={onClick}>
          {children}
        </a>
      );
    }
    return (
      <Link to={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  };

  return (
    <>
      <nav
        ref={navRef}
        className={`${styles.navbar} ${scrolled ? styles.navbarScrolled : styles.navbarLight}`}
      >
        <div className={styles.container}>
          {/* Logo */}
          <Link to="/" className={styles.logo} onClick={closeMobile}>
            <img src={logo} alt="JurisTab" className={styles.logoIcon} />
          </Link>

          {/* Desktop nav */}
          <ul className={styles.navLinks}>
            {NAV_ITEMS.map((item) => (
              <li
                key={item.label}
                className={`${styles.navItem} ${
                  item.dropdown && openDropdown === item.label ? styles.dropdownOpen : ''
                }`}
                onMouseEnter={() => item.dropdown && setOpenDropdown(item.label)}
                onMouseLeave={() => item.dropdown && setOpenDropdown(null)}
              >
                {item.dropdown ? (
                  <>
                    <button
                      className={styles.navLink}
                      onClick={() =>
                        setOpenDropdown(openDropdown === item.label ? null : item.label)
                      }
                      aria-expanded={openDropdown === item.label}
                      aria-haspopup="true"
                    >
                      {item.label}
                      <FiChevronDown />
                    </button>
                    <div className={styles.dropdown} role="menu">
                      {item.dropdown.map((sub, i) => (
                        <React.Fragment key={sub.label}>
                          {renderLink(
                            sub.href,
                            <>
                              <span className={styles.dropdownIcon}>{sub.icon}</span>
                              <span className={styles.dropdownInfo}>
                                <span className={styles.dropdownLabel}>
                                  {sub.label}
                                  {sub.external && (
                                    <FiArrowUpRight
                                      style={{ marginLeft: 4, fontSize: '0.7rem', opacity: 0.5 }}
                                    />
                                  )}
                                </span>
                                {sub.desc && (
                                  <span className={styles.dropdownDesc}>{sub.desc}</span>
                                )}
                              </span>
                            </>,
                            styles.dropdownLink,
                            sub.external,
                            () => setOpenDropdown(null)
                          )}
                          {i < item.dropdown!.length - 1 && (
                            <div className={styles.dropdownDivider} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link to={item.href!} className={styles.navLink}>
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Hamburger */}
          <button
            className={`${styles.hamburger} ${mobileOpen ? styles.hamburgerOpen : ''}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileOpen}
          >
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`${styles.mobileMenu} ${mobileOpen ? styles.mobileMenuOpen : ''}`}
        aria-hidden={!mobileOpen}
      >
        <div className={styles.mobileNavLinks}>
          {NAV_ITEMS.map((item) =>
            item.dropdown ? (
              <div
                key={item.label}
                className={mobileDropdowns.has(item.label) ? styles.mobileDropdownOpen : ''}
              >
                <button
                  className={styles.mobileNavLink}
                  onClick={() => toggleMobileDropdown(item.label)}
                  aria-expanded={mobileDropdowns.has(item.label)}
                >
                  {item.label}
                  <FiChevronDown />
                </button>
                <div className={styles.mobileDropdown}>
                  <div className={styles.mobileDropdownInner}>
                    {item.dropdown.map((sub) =>
                      renderLink(
                        sub.href,
                        <>
                          <span className={styles.mobileDropdownLinkIcon}>{sub.icon}</span>
                          <span>
                            {sub.label}
                            {sub.external && (
                              <FiArrowUpRight
                                style={{ marginLeft: 4, fontSize: '0.7rem', opacity: 0.4 }}
                              />
                            )}
                          </span>
                        </>,
                        styles.mobileDropdownLink,
                        sub.external,
                        closeMobile
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.href!}
                className={styles.mobileNavLink}
                onClick={closeMobile}
              >
                {item.label}
              </Link>
            )
          )}
        </div>
      </div>

      {/* Spacer for fixed navbar */}
      <div className={styles.navSpacer} />
    </>
  );
};

export default Navbar;
